/**
 * Detección de expedientes ISO (1 .iso + 5 sidecars) en una selección de
 * archivos. Usado por la UI para fijar automáticamente el modo de carga
 * (Documento vs Expediente) y por el ExpedienteUploadService para validar.
 */
import type { ExpedienteRole } from '../../../../domain/src/lib/ingest/expediente-upload.model';

const SIDECAR_SUFFIXES: ReadonlyArray<[string, ExpedienteRole]> = [
  ['_tree.txt', 'tree'],
  ['_hashmd5.txt', 'md5'],
  ['_hashsha1.txt', 'sha1'],
  ['_chk.txt', 'chk'],
  ['_scan_report_antivirus.txt', 'antivirus'],
];

export const REQUIRED_EXPEDIENTE_ROLES: ReadonlyArray<ExpedienteRole> = [
  'iso',
  'tree',
  'md5',
  'sha1',
  'chk',
  'antivirus',
];

/** Rol del archivo según su nombre, o null si no pertenece a un expediente. */
export function classifyRole(filename: string): ExpedienteRole | null {
  const low = filename.toLowerCase();
  if (low.endsWith('.iso')) return 'iso';
  for (const [suffix, role] of SIDECAR_SUFFIXES) {
    if (low.endsWith(suffix)) return role;
  }
  return null;
}

/** Nombre base del expediente a partir del nombre del .iso. */
export function expedienteBaseFromIso(isoName: string): string {
  return isoName.replace(/\.iso$/i, '');
}

/** Extrae el hash hex (MD5=32 / SHA1=40) del contenido de un sidecar. */
export function parseHashHex(text: string): string | null {
  const m = text.match(/\b([0-9a-fA-F]{40}|[0-9a-fA-F]{32})\b/);
  return m ? m[1].toLowerCase() : null;
}

/**
 * True si la selección forma un expediente: están los 6 roles
 * (1 .iso + 5 sidecars). Tolerante a archivos extra (se ignoran).
 */
export function isExpedienteSelection(files: readonly File[]): boolean {
  const roles = new Set<ExpedienteRole>();
  for (const f of files) {
    const r = classifyRole(f.name);
    if (r) roles.add(r);
  }
  return REQUIRED_EXPEDIENTE_ROLES.every((r) => roles.has(r));
}

/** Agrupa la selección por rol (primer match por rol). Útil para el servicio. */
export function groupByRole(files: readonly File[]): Map<ExpedienteRole, File> {
  const byRole = new Map<ExpedienteRole, File>();
  for (const f of files) {
    const r = classifyRole(f.name);
    if (r && !byRole.has(r)) byRole.set(r, f);
  }
  return byRole;
}
